import { useEffect, useMemo } from "react"
import { useDispatch } from "react-redux"
import { useIsMutating, useMutation, useMutationState, useQueryClient, type QueryClient } from "@tanstack/react-query"
import { Creators } from "model/apiWrapper"
import { useAccountAlias, useIsScheduledWorkflowsEnabled } from "resources/account"
import { useGetQuery, useInfiniteGetQuery } from "hooks/queries"
import { useNotifications } from "hooks/notifications"
import { SCHEDULED_WORKFLOWS_DEFAULT_LIMIT, SCHEDULED_WORKFLOWS_MAX_LIMIT, scheduledWorkflowActionUrl, scheduledWorkflowDeliverablesUrl, scheduledWorkflowDeliverableReadUrl, scheduledWorkflowDeliverablesUnreadCountUrl, scheduledWorkflowDeliverableUrl, scheduledWorkflowRunsUrl, scheduledWorkflowsBaseUrl, scheduledWorkflowsUrl, scheduledWorkflowUrl } from "./routes"
import type { ScheduledWorkflowLifecycleAction } from "./routes"
import { runStartOutcomeOf } from "./errors"
import type { CreateWorkflowDraft } from "./draft-contract"
import type { ScheduledWorkflow, ScheduledWorkflowDeliverable, ScheduledWorkflowDeliverableListItem, ScheduledWorkflowDeliverablesResponse, ScheduledWorkflowRun, ScheduledWorkflowRunsResponse, ScheduledWorkflowsResponse, ScheduledWorkflowUnreadCountResponse, UpdateScheduledWorkflowPayload } from "./types"

const SCHEDULED_WORKFLOWS_STALE_TIME = 60_000

const REFETCH_RETRY_MILLISECONDS = 15_000

const REFETCH_MAX_RETRIES = 4

interface ScheduledWorkflowsFilter {
  includeArchived: boolean
  enabled?: boolean
}

function scheduledWorkflowsKeyPrefix(accountAlias: string) {
  return ["scheduled-workflows", accountAlias]
}

function scheduledWorkflowDeliverablesKeyPrefix(accountAlias: string) {
  return ["scheduled-workflow-deliverables", accountAlias]
}

export function scheduledWorkflowsQueryKey(accountAlias: string, { includeArchived }: ScheduledWorkflowsFilter) {
  return [...scheduledWorkflowsKeyPrefix(accountAlias), String(includeArchived)]
}

export function useScheduledWorkflows({ includeArchived, enabled = true }: ScheduledWorkflowsFilter) {
  const accountAlias = useAccountAlias()
  const { data, dataUpdatedAt, isLoading, isError, isFetchNextPageError, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteGetQuery<ScheduledWorkflowsResponse>({
    url: scheduledWorkflowsUrl(accountAlias, { includeArchived, limit: SCHEDULED_WORKFLOWS_MAX_LIMIT }),
    key: scheduledWorkflowsQueryKey(accountAlias, { includeArchived }),
    refetchOnWindowFocus: true,
    staleTime: SCHEDULED_WORKFLOWS_STALE_TIME,
    enabled,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    getPreviousPageParam: () => undefined,
    initialPageParam: null,
  })
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isFetchNextPageError) void fetchNextPage({ cancelRefetch: false })
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage])
  const workflows = useMemo(() => data?.pages.flatMap((page) => page.workflows), [data])
  return { workflows, dataUpdatedAt, isLoading: isLoading || (hasNextPage && !isFetchNextPageError), isError, isIncomplete: isFetchNextPageError }
}

export function useRefetchScheduledWorkflowsAt(at: number | null) {
  const accountAlias = useAccountAlias()
  const queryClient = useQueryClient()
  useEffect(() => {
    if (at === null) return undefined
    const queryKey = scheduledWorkflowsKeyPrefix(accountAlias)
    let timer: ReturnType<typeof setTimeout> | undefined
    let isCancelled = false
    const schedule = (delay: number, retriesLeft: number) => {
      timer = setTimeout(async () => {
        await queryClient.invalidateQueries({ queryKey })
        if (isCancelled || retriesLeft === 0) return
        const hasFailed = queryClient.getQueryCache().findAll({ queryKey, predicate: (query) => !query.queryKey.includes(DETAIL_KEY_SEGMENT) }).some((query) => query.state.status === "error")
        if (hasFailed) schedule((REFETCH_MAX_RETRIES - retriesLeft + 1) * REFETCH_RETRY_MILLISECONDS, retriesLeft - 1)
      }, delay)
    }
    schedule(Math.max(at - Date.now(), 0), REFETCH_MAX_RETRIES)
    return () => {
      isCancelled = true
      clearTimeout(timer)
    }
  }, [at, accountAlias, queryClient])
}

export function useScheduledWorkflowsCount(): { isEnabled: boolean, count: number | null } {
  const isEnabled = useIsScheduledWorkflowsEnabled()
  const count = useScheduledWorkflows({ includeArchived: false, enabled: isEnabled }).workflows?.length ?? null
  return { isEnabled, count }
}

const DETAIL_KEY_SEGMENT = "detail"

const DELIVERABLE_CHANGING_ACTIONS: (ScheduledWorkflowLifecycleAction | "runs")[] = ["archive", "runs"]

const LIFECYCLE_SUCCESS_MESSAGES: Partial<Record<ScheduledWorkflowLifecycleAction, string>> = { archive: "Workflow archived." }

function evictSettledRuns(queryClient: QueryClient, accountAlias: string, scheduledWorkflowId: string) {
  const mutationCache = queryClient.getMutationCache()
  mutationCache
    .findAll({
      mutationKey: [...scheduledWorkflowsKeyPrefix(accountAlias), "runs"],
      predicate: (mutation) => mutation.state.variables === scheduledWorkflowId
        && mutation.state.status !== "pending"
        && !(mutation.state.status === "error" && runStartOutcomeOf(mutation.state.error) === "started"),
    })
    .forEach((mutation) => mutationCache.remove(mutation))
}

function useScheduledWorkflowPost<T>(action: ScheduledWorkflowLifecycleAction | "runs", urlOf: (accountAlias: string, scheduledWorkflowId: string) => string, errorMessage: (error: unknown) => string | null, successMessage?: string) {
  const accountAlias = useAccountAlias()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const notifications = useNotifications()
  return useMutation({
    mutationKey: [...scheduledWorkflowsKeyPrefix(accountAlias), action],
    ...(action === "runs" ? { gcTime: Infinity } : {}),
    mutationFn: (scheduledWorkflowId: string) => new Promise<T>((resolve, reject) => {
      dispatch(Creators.post(urlOf(accountAlias, scheduledWorkflowId), {}, resolve, reject))
    }),
    onSuccess: () => {
      if (successMessage != null) notifications.success(successMessage)
    },
    onError: (error) => {
      const message = errorMessage(error)
      if (message !== null) notifications.error(message)
    },
    onSettled: (_data, _error, settledWorkflowId) => {
      if (action === "runs") evictSettledRuns(queryClient, accountAlias, settledWorkflowId)
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: scheduledWorkflowsKeyPrefix(accountAlias) }),
        ...(DELIVERABLE_CHANGING_ACTIONS.includes(action) ? [queryClient.invalidateQueries({ queryKey: scheduledWorkflowDeliverablesKeyPrefix(accountAlias) })] : []),
      ])
    },
  })
}

export function useScheduledWorkflowLifecycle(action: ScheduledWorkflowLifecycleAction, errorMessage: (error: unknown) => string | null, showSuccessMessage = true) {
  return useScheduledWorkflowPost<ScheduledWorkflow>(action, (accountAlias, scheduledWorkflowId) => scheduledWorkflowActionUrl(accountAlias, scheduledWorkflowId, action), errorMessage, showSuccessMessage ? LIFECYCLE_SUCCESS_MESSAGES[action] : undefined)
}

export function useIsScheduledWorkflowMutating(action: ScheduledWorkflowLifecycleAction | "runs", scheduledWorkflowId: string) {
  const accountAlias = useAccountAlias()
  return useIsMutating({ mutationKey: [...scheduledWorkflowsKeyPrefix(accountAlias), action], predicate: (mutation) => mutation.state.variables === scheduledWorkflowId }) > 0
}

export function useIsScheduledWorkflowRunStarted(scheduledWorkflowId: string) {
  const accountAlias = useAccountAlias()
  const errors = useMutationState({
    filters: { mutationKey: [...scheduledWorkflowsKeyPrefix(accountAlias), "runs"], status: "error", predicate: (mutation) => mutation.state.variables === scheduledWorkflowId },
    select: (mutation) => mutation.state.error,
  })
  return errors.some((error) => runStartOutcomeOf(error) === "started")
}

export function useStartScheduledWorkflowRun(errorMessage: (error: unknown) => string) {
  return useScheduledWorkflowPost<ScheduledWorkflowRun>("runs", scheduledWorkflowRunsUrl, errorMessage, "Run started.")
}

export function useCreateScheduledWorkflow(errorMessage: (error: unknown) => string | null) {
  const accountAlias = useAccountAlias()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const notifications = useNotifications()
  return useMutation({
    mutationKey: [...scheduledWorkflowsKeyPrefix(accountAlias), "create"],
    mutationFn: (draft: CreateWorkflowDraft) => new Promise<ScheduledWorkflow>((resolve, reject) => {
      dispatch(Creators.post(scheduledWorkflowsBaseUrl(accountAlias), draft.payload, resolve, reject))
    }),
    onError: (error) => {
      const message = errorMessage(error)
      if (message !== null) notifications.error(message)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: scheduledWorkflowsKeyPrefix(accountAlias) }),
  })
}

export function useUpdateScheduledWorkflow(errorMessage: (error: unknown) => string | null) {
  const accountAlias = useAccountAlias()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const notifications = useNotifications()
  return useMutation({
    mutationKey: [...scheduledWorkflowsKeyPrefix(accountAlias), "update"],
    mutationFn: ({ scheduledWorkflowId, payload }: { scheduledWorkflowId: string, payload: UpdateScheduledWorkflowPayload }) => new Promise<ScheduledWorkflow>((resolve, reject) => {
      dispatch(Creators.patch(scheduledWorkflowUrl(accountAlias, scheduledWorkflowId), payload, resolve, reject))
    }),
    onError: (error) => {
      const message = errorMessage(error)
      if (message !== null) notifications.error(message)
    },
    onSettled: () => Promise.all([
      queryClient.invalidateQueries({ queryKey: scheduledWorkflowsKeyPrefix(accountAlias) }),
      queryClient.invalidateQueries({ queryKey: scheduledWorkflowDeliverablesKeyPrefix(accountAlias) }),
    ]),
  })
}

export function useScheduledWorkflowDeliverables() {
  const accountAlias = useAccountAlias()
  const enabled = useIsScheduledWorkflowsEnabled()
  return useInfiniteGetQuery<ScheduledWorkflowDeliverablesResponse>({
    url: scheduledWorkflowDeliverablesUrl(accountAlias, { limit: SCHEDULED_WORKFLOWS_DEFAULT_LIMIT }),
    key: scheduledWorkflowDeliverablesKeyPrefix(accountAlias),
    enabled,
    staleTime: SCHEDULED_WORKFLOWS_STALE_TIME,
    refetchOnWindowFocus: true,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    getPreviousPageParam: () => undefined,
    initialPageParam: null,
  })
}

export function useScheduledWorkflowRuns(scheduledWorkflowId: string | null) {
  const accountAlias = useAccountAlias()
  const enabled = useIsScheduledWorkflowsEnabled()
  return useInfiniteGetQuery<ScheduledWorkflowRunsResponse>({
    url: scheduledWorkflowRunsUrl(accountAlias, scheduledWorkflowId ?? "", { limit: SCHEDULED_WORKFLOWS_DEFAULT_LIMIT }),
    key: [...scheduledWorkflowsKeyPrefix(accountAlias), DETAIL_KEY_SEGMENT, scheduledWorkflowId ?? "", "runs"],
    enabled: enabled && scheduledWorkflowId != null,
    staleTime: SCHEDULED_WORKFLOWS_STALE_TIME,
    refetchOnWindowFocus: true,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    getPreviousPageParam: () => undefined,
    initialPageParam: null,
  })
}

export function useScheduledWorkflowDeliverable(deliverableId: string | null) {
  const accountAlias = useAccountAlias()
  const enabled = useIsScheduledWorkflowsEnabled()
  return useGetQuery<ScheduledWorkflowDeliverable>({
    url: scheduledWorkflowDeliverableUrl(accountAlias, deliverableId ?? ""),
    key: [...scheduledWorkflowDeliverablesKeyPrefix(accountAlias), deliverableId],
    enabled: enabled && deliverableId != null,
  })
}

export function useMarkScheduledWorkflowDeliverableRead(errorMessage: (error: unknown) => string | null) {
  const accountAlias = useAccountAlias()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const notifications = useNotifications()
  return useMutation({
    mutationKey: [...scheduledWorkflowDeliverablesKeyPrefix(accountAlias), "read"],
    mutationFn: (deliverableId: string) => new Promise<ScheduledWorkflowDeliverableListItem>((resolve, reject) => {
      dispatch(Creators.post(scheduledWorkflowDeliverableReadUrl(accountAlias, deliverableId), {}, resolve, reject))
    }),
    onError: (error) => {
      const message = errorMessage(error)
      if (message !== null) notifications.error(message)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: scheduledWorkflowDeliverablesKeyPrefix(accountAlias) }),
  })
}

export function useScheduledWorkflowDeliverablesUnreadCount() {
  const accountAlias = useAccountAlias()
  const enabled = useIsScheduledWorkflowsEnabled()
  return useGetQuery<ScheduledWorkflowUnreadCountResponse>({
    url: scheduledWorkflowDeliverablesUnreadCountUrl(accountAlias),
    key: [...scheduledWorkflowDeliverablesKeyPrefix(accountAlias), "unread-count"],
    enabled,
    staleTime: SCHEDULED_WORKFLOWS_STALE_TIME,
    refetchOnWindowFocus: true,
  })
}

export function useScheduledWorkflow(scheduledWorkflowId: string | null) {
  const accountAlias = useAccountAlias()
  const enabled = useIsScheduledWorkflowsEnabled()
  return useGetQuery<ScheduledWorkflow>({
    url: scheduledWorkflowUrl(accountAlias, scheduledWorkflowId ?? ""),
    key: [...scheduledWorkflowsKeyPrefix(accountAlias), DETAIL_KEY_SEGMENT, scheduledWorkflowId],
    enabled: enabled && scheduledWorkflowId != null,
  })
}
